const YOUTUBE_EMBED_ORIGIN = "https://www.youtube.com/embed/";
const YOUTUBE_EMBED_AUTOPLAY = "?autoplay=1";
const YOUTUBE_VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/u;
const GOOGLE_PLAY_HOST = "play.google.com";
const YOUTUBE_SHORTS_PATH = "shorts";
const GOOGLE_PLAY_PLAYER_PATTERN =
  /^\/video\/lava\/web\/player\/yt:movie:([A-Za-z0-9_-]{11})$/u;
const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
]);
const FACEBOOK_HOSTS = new Set([
  "facebook.com",
  "www.facebook.com",
  "m.facebook.com",
]);
const FACEBOOK_VIDEO_PATH_PATTERN = /\/videos\/.+\/\d+\/?$/u;

export interface CresultcardYoutubeSource {
  readonly embedUrl: string;
  readonly videoId: string;
}

function parsedHttpsUrl(sourceHref: string): URL | null {
  try {
    const parsed = new URL(sourceHref);
    return parsed.protocol === "https:" &&
      parsed.username.length === 0 &&
      parsed.password.length === 0 &&
      parsed.port.length === 0
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function sourceVideoId(source: URL): string | null {
  if (YOUTUBE_HOSTS.has(source.hostname) && source.pathname === "/watch") {
    const identities = source.searchParams.getAll("v");
    return identities.length === 1 ? (identities[0] ?? null) : null;
  }
  if (YOUTUBE_HOSTS.has(source.hostname)) {
    const path = source.pathname.split("/").filter(Boolean);
    return path.length === 2 && path[0] === YOUTUBE_SHORTS_PATH
      ? (path[1] ?? null)
      : null;
  }
  if (source.hostname === GOOGLE_PLAY_HOST) {
    return GOOGLE_PLAY_PLAYER_PATTERN.exec(source.pathname)?.[1] ?? null;
  }
  return null;
}

/** A strict Facebook video-page relation mapped to Facebook's iframe endpoint. */
export function cresultcardFacebookVideoEmbedUrl(
  sourceHref: string,
): string | null {
  const source = parsedHttpsUrl(sourceHref);
  if (
    source === null ||
    !FACEBOOK_HOSTS.has(source.hostname) ||
    !FACEBOOK_VIDEO_PATH_PATTERN.test(source.pathname)
  ) {
    return null;
  }
  const embed = new URL("https://www.facebook.com/plugins/video.php");
  embed.searchParams.set("href", sourceHref);
  embed.searchParams.set("show_text", "0");
  return embed.href;
}

/**
 * Derives a YouTube embed only from an exact captured YouTube watch address or
 * Google Play's exact `yt:movie` player address. The source address is never
 * rewritten; callers retain it separately as the outbound evidence link.
 */
export function cresultcardYoutubeSource(
  sourceHref: string,
): CresultcardYoutubeSource | null {
  const source = parsedHttpsUrl(sourceHref);
  if (source === null) return null;
  const videoId = sourceVideoId(source);
  if (videoId === null || !YOUTUBE_VIDEO_ID_PATTERN.test(videoId)) return null;
  return {
    embedUrl: `${YOUTUBE_EMBED_ORIGIN}${videoId}${YOUTUBE_EMBED_AUTOPLAY}`,
    videoId,
  };
}
